import * as React from "react";
import { Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

import { ProductChainDetails } from "./services/types";
import { DATA_PLATFORM_TYPE } from "../../common/constants";
import { appendIPFSUrl, formatAddress, FormatDate } from "../../common/utils";

interface InfoAccordionProps {
  productChainDetails?: ProductChainDetails;
}

export default function InfoAccordion({
  productChainDetails,
}: InfoAccordionProps) {
  return (
    <div className="infoAccordionWrapper">
      {/* BLOCKCHAIN INFO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h4 className="accordionTitle">Blockchain Info</h4>
        </AccordionSummary>

        <AccordionDetails>
          Product batch details are stored using IPFS (InterPlanetary File
          System), a decentralized and tamper-resistant storage network.
          Combined with blockchain records, this ensures the data is immutable,
          independently verifiable, and not dependent on any single company or
          server — including Databis. This approach reflects our commitment to
          transparency, security, and long-term data integrity.
          <Link
            target="_blank"
            className="link"
            to={appendIPFSUrl(productChainDetails?.coaNftIpfsHash ?? "#")}
          >
            Click here to view this data on IPFS
          </Link>
          <div className="listDivider"></div>
          <ul className="listWrapper">
            {/* Contract Address */}
            <li className="list">
              <span className="key fontGrey">Contract Address:</span>
              <Link
                to={
                  productChainDetails?.blockchainInfo
                    ?.contractAddressRedirectUrl ?? "#"
                }
                className="added-link"
                target="_blank"
              >
                <span className="value">
                  {productChainDetails?.blockchainInfo
                    ?.contractAddressRedirectUrl
                    ? formatAddress(
                        productChainDetails?.blockchainInfo
                          ?.contractAddressRedirectUrl,
                      )
                    : "-"}
                </span>
              </Link>
            </li>

            {/* Token ID */}
            <li className="list">
              <span className="key fontGrey">Token ID:</span>
              <Link
                to={
                  productChainDetails?.blockchainInfo?.tokenIdRedirectUrl ?? "#"
                }
                className="added-link"
                target="_blank"
              >
                <span className="value">
                  {productChainDetails?.blockchainInfo?.tokenId
                    ? productChainDetails?.blockchainInfo?.tokenId
                    : "-"}
                </span>
              </Link>
            </li>

            {/* Token Standard */}
            <li className="list">
              <span className="key fontGrey">Token Standard:</span>
              <span className="value">
                {productChainDetails?.blockchainInfo?.tokenStandard
                  ? productChainDetails?.blockchainInfo?.tokenStandard
                  : "-"}
              </span>
            </li>

            {/* Chain */}
            <li className="list">
              <span className="key fontGrey">Chain:</span>
              <span className="value">
                {productChainDetails?.blockchainInfo?.chainName
                  ? productChainDetails?.blockchainInfo?.chainName
                  : "-"}
              </span>
            </li>

            {productChainDetails?.blockchainInfo?.regulatorBatchId ? (
              <li className="list">
                <span className="key fontGrey">Regulator Batch Id:</span>
                <span className="value">
                  {productChainDetails?.dataPlatform ===
                  DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                    ? "-"
                    : productChainDetails?.blockchainInfo?.regulatorBatchId}
                </span>
              </li>
            ) : null}
          </ul>
        </AccordionDetails>
      </Accordion>

      {/* PRIMARY INFO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <h4 className="accordionTitle">Primary Information</h4>
        </AccordionSummary>

        <AccordionDetails>
          {/* Basic Product Info */}
          <ul className="listWrapper">
            <li className="list">
              <span className="key fontGrey">Strain Name:</span>
              <span className="value">
                {(productChainDetails?.primaryInfo?.strainName ??
                productChainDetails?.strainName)
                  ? (productChainDetails?.primaryInfo?.strainName ??
                    productChainDetails?.strainName)
                  : "-"}
              </span>
            </li>

            <li className="list">
              <span className="key fontGrey">CoA ID:</span>
              <span className="value coaId">
                {productChainDetails?.primaryInfo?.coaId
                  ? productChainDetails?.primaryInfo?.coaId
                  : "-"}
              </span>
            </li>

            <li className="list">
              <span className="key fontGrey">Batch Index:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.batchIndexId
                  ? productChainDetails?.primaryInfo?.batchIndexId
                  : "-"}
              </span>
            </li>
          </ul>

          <div className="listDivider"></div>

          {/* Manufacturer Info */}
          <ul className="listWrapper">
            <li className="list">
              <span className="key fontGrey">Production State:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.cultivationState
                  ? productChainDetails?.primaryInfo?.cultivationState
                  : "-"}
              </span>
            </li>

            <li className="list">
              <span className="key fontGrey">Manufacturer:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.cultivatorName
                  ? productChainDetails?.primaryInfo?.cultivatorName
                  : "-"}
              </span>
            </li>

            {/* <li className="list">
              <span className="key fontGrey">Quantity:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.totalProductCount
                  ? productChainDetails?.primaryInfo?.totalProductCount
                  : "-"}
              </span>
            </li> */}
          </ul>

          <div className="listDivider"></div>

          {/* Product Type */}
          <ul className="listWrapper">
            <li className="list">
              <span className="key fontGrey">Product Type:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.productType
                  ? productChainDetails?.primaryInfo?.productType
                  : "-"}
              </span>
            </li>

            <li className="list">
              <span className="key fontGrey">Product Sub-type:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.productSubType
                  ? productChainDetails?.primaryInfo?.productSubType
                  : "-"}
              </span>
            </li>
          </ul>

          <div className="listDivider"></div>

          {/* Dates Info */}
          <ul className="listWrapper">
            <li className="list">
              <span className="key fontGrey">Manufactured Date:</span>
              <span className="value">
                {productChainDetails?.primaryInfo?.harvestDate
                  ? FormatDate(productChainDetails?.primaryInfo?.harvestDate)
                  : "-"}
              </span>
            </li>
          </ul>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
